import React from 'react';
import { Box, Button, ButtonBase, IconButton, Tooltip } from '@mui/material';
import {
  Add as AddIcon,
  DarkModeOutlined as DarkIcon,
  LightModeOutlined as LightIcon,
  Logout as LogoutIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BrandMark } from './BrandMark';
import { NAV_ITEMS, sectionForPath } from './appNavItems';
import { StravaSyncButton } from './StravaSyncButton';
import { LanguageToggle } from './LanguageToggle';
import { ShellGroup, displayNameOf } from './useAppShellData';
import { InitialsAvatar } from '../ui/InitialsAvatar';
import { useColorMode } from '../../theme/ColorModeProvider';
import { authService } from '../../services/auth.service';
import { UserProfile } from '../../types/user.types';
import { STRAVA_ORANGE } from '../../theme/appColorTokens';

interface AppSidebarProps {
  profile: UserProfile | null;
  myGroups: ShellGroup[];
  onNewChallenge: () => void;
}

const squareBtnSx = { width: 30, height: 30, border: '1px solid var(--border)', borderRadius: '7px', backgroundColor: 'var(--card)', color: 'var(--fg)' } as const;

export const AppSidebar = ({ profile, myGroups, onNewChallenge }: AppSidebarProps) => {
  const { t } = useTranslation('layout');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { mode, toggleMode } = useColorMode();
  const section = sectionForPath(pathname);
  const name = displayNameOf(profile);
  const stravaConnected = profile?.stravaProfile != null;

  return (
    <Box
      component="aside"
      sx={{ width: 252, flex: 'none', borderRight: '1px solid var(--border)', backgroundColor: 'var(--card)', display: 'flex', flexDirection: 'column', p: '16px 12px', gap: '18px', overflowY: 'auto', minHeight: 0 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1, py: 0.5 }}>
        <BrandMark size={32} />
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box component="span" sx={{ fontWeight: 700, fontSize: 15, letterSpacing: '.02em' }}>URACE</Box>
          <Box component="span" sx={{ fontSize: 12, color: 'var(--muted)' }}>{t('common:brand.tagline')}</Box>
        </Box>
      </Box>

      <Button variant="contained" startIcon={<AddIcon />} onClick={onNewChallenge}>
        {t('sidebar.newChallenge')}
      </Button>

      <Box component="nav" sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {NAV_ITEMS.map(item => {
          const active = section === item.key;
          return (
            <ButtonBase
              key={item.key}
              onClick={() => navigate(item.to)}
              aria-current={active ? 'page' : undefined}
              sx={{
                height: 36, borderRadius: '8px', px: 1.25, justifyContent: 'flex-start', gap: 1.25, fontSize: 14, fontWeight: 500,
                backgroundColor: active ? 'var(--accent-bg)' : 'transparent',
                color: active ? 'var(--accent-fg)' : 'var(--muted)',
                '&:hover': { backgroundColor: active ? 'var(--accent-bg)' : 'var(--muted-bg)' },
                '& svg': { fontSize: 18 },
              }}
            >
              {item.icon}
              {t(item.labelKey)}
            </ButtonBase>
          );
        })}
      </Box>

      {myGroups.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ fontSize: 12, fontWeight: 500, color: 'var(--muted)', px: 1.25, pb: 0.5 }}>{t('sidebar.yourGroups')}</Box>
          {myGroups.map(g => (
            <ButtonBase
              key={g._id}
              onClick={() => navigate(`/groups/${g._id}`)}
              sx={{ height: 32, borderRadius: '8px', px: 1.25, justifyContent: 'flex-start', gap: 1.25, fontSize: 13, color: 'var(--fg)', textAlign: 'left', '&:hover': { backgroundColor: 'var(--muted-bg)' } }}
            >
              <InitialsAvatar name={g.name} size={18} shape="rounded" />
              <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.name}</Box>
            </ButtonBase>
          ))}
        </Box>
      )}

      <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {profile && (
          <Box sx={{ border: '1px solid var(--border)', borderRadius: '10px', p: 1.5, display: 'flex', gap: 1.25, alignItems: 'center', fontSize: 12 }}>
            <Box component="span" sx={{ width: 8, height: 8, borderRadius: '999px', flex: 'none', backgroundColor: stravaConnected ? '#22c55e' : 'var(--muted)' }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
              <Box component="span" sx={{ fontWeight: 500, fontSize: 13 }}>{stravaConnected ? t('strava.connected') : t('strava.notConnected')}</Box>
              <Box component="span" sx={{ color: 'var(--muted)' }}>{stravaConnected ? t('strava.autoSync') : t('strava.connectHint')}</Box>
            </Box>
            <Box sx={{ ml: 'auto' }}>
              {stravaConnected ? (
                <StravaSyncButton />
              ) : (
                <Tooltip title={t('strava.connect')}>
                  <IconButton onClick={() => navigate('/connect-strava')} aria-label={t('strava.connect')} sx={{ ...squareBtnSx, width: 28, height: 28, color: STRAVA_ORANGE }}>
                    <LinkIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 0.75, py: 0.5 }}>
          <InitialsAvatar name={name || '?'} size={32} />
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
            <Box component="span" sx={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</Box>
            {profile && <Box component="span" sx={{ fontSize: 12, color: 'var(--muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>@{profile.username}</Box>}
          </Box>
          <LanguageToggle size={30} />
          <Tooltip title={mode === 'dark' ? t('common:theme.light') : t('common:theme.dark')}>
            <IconButton onClick={toggleMode} aria-label={t('common:theme.toggle')} sx={squareBtnSx}>
              {mode === 'dark' ? <LightIcon sx={{ fontSize: 15 }} /> : <DarkIcon sx={{ fontSize: 15 }} />}
            </IconButton>
          </Tooltip>
          <Tooltip title={t('account.logout')}>
            <IconButton onClick={() => authService.logout()} aria-label={t('account.logout')} sx={squareBtnSx}>
              <LogoutIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};
